import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, X, Send } from "lucide-react";
import { api } from "@/lib/api";

const SUGGESTIONS = ["What's popular on the menu?", "Do you cater weddings?", "What are your hours?", "How do I order online?"];
const sid = "sero-" + Math.random().toString(36).slice(2);

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi, I'm Ember — your Sero's concierge. Ask me about the menu, catering, hours or ordering. 🔥" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(api.chatUrl, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          try {
            const data = JSON.parse(line);
            if (data.delta) setMessages((m) => { const c = [...m]; c[c.length - 1].content += data.delta; return c; });
          } catch { /* ignore */ }
        }
      }
    } catch {
      setMessages((m) => { const c = [...m]; c[c.length - 1].content = "Sorry, I had trouble connecting. Please call (248) 544-1810."; return c; });
    } finally { setBusy(false); }
  };

  return (
    <>
      <motion.button data-testid="ai-fab" onClick={() => setOpen((v) => !v)}
        whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
        className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-50 h-14 w-14 rounded-full bg-terracotta text-cream shadow-[0_10px_30px_-8px_rgba(192,90,53,0.7)] flex items-center justify-center">
        {open ? <X /> : <Flame />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div data-testid="ai-panel"
            initial={{ opacity: 0, y: 20, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-36 md:bottom-24 right-4 md:right-6 z-50 w-[calc(100vw-2rem)] max-w-sm h-[30rem] bg-cream rounded-2xl overflow-hidden shadow-2xl border border-ink/10 flex flex-col">
            <div className="bg-charcoal text-cream px-5 py-4 flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-terracotta flex items-center justify-center"><Flame size={18} /></div>
              <div><p className="font-serif-display text-xl leading-none">Ember</p><p className="text-cream/50 text-xs mt-0.5">Sero's Concierge</p></div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[82%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-terracotta text-cream rounded-br-sm" : "bg-sand text-ink rounded-bl-sm"}`}>
                    {m.content || <span className="opacity-50">…</span>}
                  </div>
                </div>
              ))}
              {messages.length <= 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} data-testid="ai-suggestion" onClick={() => send(s)} className="text-xs border border-ink/15 text-ink/70 rounded-full px-3 py-1.5 hover:border-terracotta hover:text-terracotta transition-colors">{s}</button>
                  ))}
                </div>
              )}
            </div>

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-ink/10 flex gap-2">
              <input data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask Ember…"
                className="flex-1 bg-sand/60 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-1 focus:ring-terracotta" />
              <button type="submit" disabled={busy} data-testid="ai-send" className="h-10 w-10 shrink-0 rounded-full bg-terracotta text-cream flex items-center justify-center disabled:opacity-50 active:scale-95 transition-transform"><Send size={16} /></button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
